/**
 * Typed relay failures for the ciphertext endpoints. Only the relay's short error code crosses
 * into the UI (never a response body), so messages stay meaningful without echoing content.
 */
import { RATE_LIMIT_CODES, RATE_LIMITED } from '@viko/protocol'
import { retryAfterHeader } from './request-meter.js'

const MESSAGES: Record<string, string> = {
  content_provisioning_required:
    'Encrypted storage is not set up yet. Add a project or start a run on a trusted device to set it up.',
  unauthenticated: 'Your session ended. Sign in again to load encrypted content.',
  insufficient_scope: 'Sign in with a passkey to load encrypted content.',
  wrong_account: 'This browser is signed in to a different account. Sign in again.',
  stale_content_epoch: 'Encryption keys changed. Reload to catch up.',
  directory_requires_rotation:
    'A device was revoked. Open Viko on a trusted device to rotate keys.',
  too_large: 'The encrypted request was too large.',
  rate_limited: 'Viko is pacing requests for a moment. It retries on its own.',
  budget_degraded: 'Viko reached its daily request budget; background refreshes are paused.',
}
const CODE = /^[a-z0-9_]{1,64}$/

export class ContentRequestError extends Error {
  /** burst-guard: set for a rate-limited answer (429 body `retryAfterMs` or Retry-After). */
  retryAfterMs?: number
  constructor(
    readonly code: string,
    readonly status: number,
    fallback: string,
  ) {
    super(
      MESSAGES[code] ??
        (code.startsWith('quota_') ? 'Account storage limit reached.' : `${fallback} (${code})`),
    )
    this.name = 'ContentRequestError'
  }
}

/** Relay reports this account has no signed content directory yet (never a trusted fact alone). */
export const isUnprovisioned = (e: unknown) =>
  e instanceof ContentRequestError && e.code === 'content_provisioning_required'

/** Read at most 1 KiB of an error body and keep only a well-formed `{ error: code }`. */
export async function contentRequestError(response: Response, fallback: string) {
  let code = `http_${response.status}`
  let retryAfterMs: number | undefined
  try {
    const reader = response.body?.getReader()
    if (reader) {
      const parts: Uint8Array[] = []
      let size = 0
      try {
        for (;;) {
          const part = await reader.read()
          if (part.done) break
          size += part.value.byteLength
          if (size > 1024) {
            await reader.cancel()
            parts.length = 0
            break
          }
          parts.push(part.value)
        }
      } finally {
        reader.releaseLock()
      }
      if (parts.length) {
        const bytes = new Uint8Array(size)
        let offset = 0
        for (const p of parts) {
          bytes.set(p, offset)
          offset += p.byteLength
        }
        const body = JSON.parse(new TextDecoder().decode(bytes)) as {
          error?: unknown
          code?: unknown
          retryAfterMs?: unknown
        }
        const named = typeof body.error === 'string' ? body.error : body.code
        if (typeof named === 'string' && CODE.test(named)) code = named
        if (typeof body.retryAfterMs === 'number' && body.retryAfterMs >= 0)
          retryAfterMs = Math.min(86_400_000, body.retryAfterMs)
      }
    }
  } catch {
    // Malformed or oversized error bodies keep the status-only code.
  }
  if (response.status === 429 && !RATE_LIMIT_CODES.has(code)) code = RATE_LIMITED
  const error = new ContentRequestError(code, response.status, fallback)
  if (response.status === 429)
    error.retryAfterMs = retryAfterMs ?? retryAfterHeader(response) ?? 1_000
  return error
}

// ---- screen-sweep: features the hub refuses for end-to-end encrypted accounts ----
/** Hub codes for a legacy plaintext lane refused because this account is end-to-end encrypted. */
export const ENCRYPTED_LANE_CODES = new Set([
  'encrypted_content_required',
  'encrypted_work_required',
])
export const ENCRYPTED_UNAVAILABLE_TEXT =
  'Not available for end-to-end encrypted accounts yet. Everything else keeps working; this arrives in a later update.'
/** A plaintext-only feature, refused locally so the request never leaves the browser. */
export class EncryptedUnavailable extends Error {
  readonly code = 'encrypted_unavailable'
  constructor(readonly feature: string) {
    super(`${feature}: ${ENCRYPTED_UNAVAILABLE_TEXT}`)
    this.name = 'EncryptedUnavailable'
  }
}
/** True for a refused legacy lane (hub code) or a locally refused plaintext-only feature. */
export function isEncryptedUnavailable(e: unknown): boolean {
  if (e instanceof EncryptedUnavailable) return true
  const code = (e as { code?: unknown } | null)?.code
  return typeof code === 'string' && ENCRYPTED_LANE_CODES.has(code)
}
// ---- end screen-sweep ----
